import React from 'react';

import { OpalAuth } from '@frontend/atoms';
import type { MessagingViewState } from '@frontend/constants';

type Payload = {
  content: string;
  role: 'system' | 'user';
};

type PromptPayload = {
  type: string;
  payload: Payload[];
};

export type PromptQueue = {
  enqueuePrompt: (prompt: PromptPayload) => Promise<void>;
};

export type SuggestionItem = {
  prompt: string;
  title: string;
};

// Inbound counterpart of the stateChanged snapshot; Partial since the host may
// push a subset (e.g. just `mode`).
export type InitialMessaging = Partial<MessagingViewState>;

export type AIChatComponentProps = {
  currentUser: {
    instanceId: string;
    avatarUrl: undefined | string;
    name: string;
    operations?: string[];
  };
  storeKey: string;
  productScope: string;
  showHeaderIcons: boolean;
  sidebarAlwaysVisible: boolean;
  initialDockState: string;
  initialPrompt?: string;
  initialThreadId?: string | null;
  initialMessaging?: InitialMessaging;
  hideChatModeTabs?: boolean;
  initialMessageId?: string | null;
  initialSuggestions?: SuggestionItem[];
  opalAuth?: OpalAuth;
  voiceDictationEnabled?: boolean;
  allowPopout?: boolean;
};

// Kick off the remote chunk download at module-eval time (when something first
// imports this file) instead of waiting for React.lazy's factory to run on first
// render. This lets the OpalChat/components fetch proceed in parallel with other
// startup work (e.g. OpalAuth). React.lazy then reuses the in-flight promise.
// @ts-expect-error - External module import
const OpalChatComponentsPromise = import('OpalChat/components')
  .then(m => ({ default: m.AIChatComponent }))
  .catch((error: unknown) => {
    console.error('Failed to load OpalChat components:', error);
    return { default: () => <div>Chat component failed to load</div> };
  });

export const LazyAIChatComponent = React.lazy<React.ComponentType<AIChatComponentProps>>(
  () => OpalChatComponentsPromise
);
